import { useEffect, useRef, useState } from 'react';
import { GameEngine } from '@/canvas/systems/GameEngine';
import { useGameStore } from '@/store/gameStore';

type UseGameEngineParams = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isEnabled: boolean;
  enableBlockMerge?: boolean;
  onGameEnd: (
    finalScore: number,
    blockCounts: Record<string, number>,
  ) => void | Promise<void>;
  onInitError?: (error: unknown) => void;
};

export const useGameEngine = ({
  containerRef,
  isEnabled,
  enableBlockMerge = true,
  onGameEnd,
  onInitError,
}: UseGameEngineParams) => {
  const engineRef = useRef<GameEngine | null>(null);
  const onGameEndRef = useRef(onGameEnd);
  const onInitErrorRef = useRef(onInitError);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    onGameEndRef.current = onGameEnd;
  }, [onGameEnd]);

  useEffect(() => {
    onInitErrorRef.current = onInitError;
  }, [onInitError]);

  useEffect(() => {
    if (!isEnabled) {
      setIsLoading(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    setIsLoading(true);

    const engine = new GameEngine(container, {
      callbacks: {
        onGameEnd: (finalScore: number) => {
          const blockCounts = useGameStore.getState().blockCounts;
          onGameEndRef.current(finalScore, blockCounts);
        },
      },
      enableBlockMerge,
    });

    engineRef.current = engine;

    engine
      .init()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to initialize game:', error);
        onInitErrorRef.current?.(error);
        setIsLoading(false);
      });

    return () => {
      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
      }
    };
  }, [containerRef, enableBlockMerge, isEnabled]);

  return { engineRef, isLoading };
};
